// src/admin/adminApi.js
import i18n from '../i18n/index.js';
import { apiFetch } from '../auth/apiFetch.js';
import { buildApiError } from '../context/parseApiError.js';

const ADMIN = '/api/v1/admin';

// Superadmin calls throw a buildApiError (err.code / err.status / err.requestId). ⛔ Its message is still the
// server's English text — the console words every failure from the code (admin/adminErrorCopy.js), never prints it.
// readAdminError (below, hoisted) unwraps a {detail: {...}} envelope.
async function adminError(res, what) {
  const body = await readAdminError(res);
  return buildApiError(body, res.status, { fallbackMessage: `${what}: ${res.status}` });
}

// ── Users ──────────────────────────────────────────────────────────
export async function fetchUsers({ limit = 20, offset = 0, email, tenant } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (email) params.set('email', email);
  if (tenant) params.set('tenant', tenant);
  const res = await apiFetch(`${ADMIN}/users?${params}`);
  if (!res.ok) throw await adminError(res, 'Failed to fetch users');
  return res.json();
}

export async function createUser({ email, password }) {
  const res = await apiFetch(`${ADMIN}/users`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to create user: ${res.status}`);
  }
  return res.json();
}

export async function deactivateUser(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/deactivate`, { method: 'POST' });
  if (!res.ok) throw await adminError(res, 'Failed to deactivate');
  return res.json();
}

export async function activateUser(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/activate`, { method: 'POST' });
  if (!res.ok) throw await adminError(res, 'Failed to activate');
  return res.json();
}

export async function fetchUser(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}`);
  if (!res.ok) throw await adminError(res, 'Failed to fetch user');
  return res.json();
}

export async function updateUserEmail(userId, email) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/email`, {
    method: 'PATCH',
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw await adminError(res, 'Failed to update email');
  return res.json();
}

export async function resetUserPassword(userId, password) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to reset password: ${res.status}`);
  }
}

export async function grantSuperadmin(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/grant-superadmin`, { method: 'POST' });
  if (!res.ok) throw await adminError(res, 'Failed to grant superadmin');
  return res.json();
}

export async function revokeSuperadmin(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/revoke-superadmin`, { method: 'POST' });
  if (!res.ok) throw await adminError(res, 'Failed to revoke superadmin');
  return res.json();
}

export async function fetchUserTenants(userId) {
  const res = await apiFetch(`${ADMIN}/users/${userId}/tenants`);
  if (!res.ok) throw await adminError(res, 'Failed to fetch user tenants');
  return res.json();
}

// The person's companies (`customer_users`): [{customer_id, legal_name, role, is_active}]. An `admin` row reaches
// every workspace of the company with no workspace membership. An older backend answers 404 here.
export async function fetchUserCustomers(userId) {
  const res = await apiFetch(`${ADMIN}/users/${encodeURIComponent(userId)}/customers`);
  if (!res.ok) throw await adminError(res, 'Failed to fetch user companies');
  return res.json();
}

export async function assignUserToTenant(tenantId, email, role = 'editor') {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/users`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Structured so the caller can branch on err.code. ⛔ This route is NOT
    // seat-gated on the active backend — unlike the two tenant-admin twins in
    // tenantAdminApi.js it emits no seat refusal, so callers must not word one.
    throw buildApiError(body, res.status, { fallbackMessage: `Failed to assign user: ${res.status}` });
  }
}

export async function removeUserFromTenant(tenantId, userId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/users/${userId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw await adminError(res, 'Failed to remove user');
  return res.json();
}

export async function changeUserRole(tenantId, userId, role) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
  if (!res.ok) throw await adminError(res, 'Failed to change role');
}

export async function fetchApplications() {
  const res = await apiFetch(`${ADMIN}/applications`);
  if (!res.ok) throw await adminError(res, 'Failed to fetch apps');
  return res.json();
}

// Self-service (any authenticated user, NOT admin-only)
export async function changeOwnPassword(currentPassword, newPassword) {
  const res = await apiFetch('/api/v1/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  // A buildApiError (code / status / requestId): Profilo words it — authErrorCopy.js.
  if (!res.ok) throw buildApiError(await res.json().catch(() => ({})), res.status);
}

// Self-service profile edit (any authenticated user, edits SELF only).
// PATCH semantics: pass ONLY the keys the user actually changed. An omitted key
// is left unchanged; an explicit null clears the field. Never dump the whole
// form — an untouched empty field sent as null would wipe stored data.
// Returns the full MeResponse so callers can refresh local user state.
export async function updateMe(patch) {
  const res = await apiFetch('/api/v1/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  // A buildApiError (code / status / requestId): Profilo words it — authErrorCopy.js.
  if (!res.ok) throw buildApiError(await res.json().catch(() => ({})), res.status);
  return res.json();
}

export async function assignAppToTenant(tenantId, applicationId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/apps`, {
    method: 'POST',
    body: JSON.stringify({ application_id: applicationId }),
  });
  if (!res.ok) throw await adminError(res, 'Failed to assign app');
}

// ── Admin tenants (read-only) ──────────────────────────────────────
// `includeInactive` lists retired tenants too — only the tenant page asks (the other pickers stay active-only).
export async function adminListTenants({ includeInactive = false } = {}) {
  const res = await apiFetch(`${ADMIN}/tenants${includeInactive ? '?include_inactive=true' : ''}`);
  if (!res.ok) throw await adminError(res, 'Failed to list tenants');
  return res.json();
}

// ── Tenant lifecycle and purge (superadmin; backend ≥ 554ad59) ─────────────
// Retire = `is_active:false`: new runs refused at once, members lose it at their next sign-in, data and seat kept.
// Both are idempotent and answer the tenant row. ⛔ `default` cannot be retired (409 TENANT_PROTECTED).
export async function adminDeactivateTenant(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${encodeURIComponent(tenantId)}/deactivate`, { method: 'POST' });
  if (!res.ok) throw await adminError(res, 'Failed to retire tenant');
  return res.json();
}

export async function adminActivateTenant(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${encodeURIComponent(tenantId)}/activate`, { method: 'POST' });
  if (!res.ok) throw await adminError(res, 'Failed to reactivate tenant');
  return res.json();
}

// Purge: a read-only preview, then a POST whose body names the target again. ⛔ Irreversible. ⛔ These routes refuse
// any undeclared query parameter and any extra body field (422) — the company preview's `?mode=` is declared (see
// admin/companiesApi.js). Contract: <BACKEND_FOLDER>/docs/api-server/endpoints.md#purge
const PURGE = `${ADMIN}/purge`;

export async function previewTenantPurge(tenantId) {
  const res = await apiFetch(`${PURGE}/tenants/${encodeURIComponent(tenantId)}/preview`);
  if (!res.ok) throw await adminError(res, 'Failed to preview tenant purge');
  return res.json();
}

// `confirm` must equal the tenant id exactly.
export async function purgeTenant(tenantId, confirm) {
  const res = await apiFetch(`${PURGE}/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'POST',
    body: JSON.stringify({ confirm }),
  });
  if (!res.ok) throw await adminError(res, 'Failed to purge tenant');
  return res.json();
}

export async function previewUserPurge(userId) {
  const res = await apiFetch(`${PURGE}/users/${encodeURIComponent(userId)}/preview`);
  if (!res.ok) throw await adminError(res, 'Failed to preview user purge');
  return res.json();
}

// `confirm` = the account's current email; the backend compares case-insensitively WITHOUT trimming, so it is sent
// exactly as typed.
export async function purgeUser(userId, confirm) {
  const res = await apiFetch(`${PURGE}/users/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify({ confirm }),
  });
  if (!res.ok) throw await adminError(res, 'Failed to purge user');
  return res.json();
}

export async function adminGetTenant(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to fetch tenant: ${res.status}`);
  }
  return res.json();
}

export async function adminListTenantUsers(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/users`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to list tenant users: ${res.status}`);
  }
  return res.json();
}

export async function adminListTenantApps(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/apps`);
  if (!res.ok) throw await adminError(res, 'Failed to list tenant apps');
  return res.json();
}

// ── Plans & subscriptions (Credits — superadmin only) ──────────────
// Tolerant error parse: unwrap body.detail when present, else read top-level
// (matches the rest of this file's top-level convention; survives either shape).
async function readAdminError(res) {
  const body = await res.json().catch(() => ({}));
  return body && body.detail && typeof body.detail === 'object' ? body.detail : body;
}

// Normalise list responses: BE may return a bare array or { plans: [...] }.
export async function adminListPlans() {
  const res = await apiFetch(`${ADMIN}/plans`);
  if (!res.ok) {
    const err = await readAdminError(res);
    throw new Error(err.message || `Failed to list plans: ${res.status}`);
  }
  const data = await res.json();
  return Array.isArray(data) ? data : (data.plans || []);
}

// ── Plan write payloads ─────────────────────────────────────────────
//
// The plan model grew well past { display_name, monthly_credits, active }, and
// the create path had two hard bugs that made it 422 on EVERY attempt since it
// shipped (creation has never once worked):
//
//   1. `id` was never sent. It is the plan's slug primary key and is MANDATORY
//      on create — the BE has no generator for it.
//   2. `active` WAS sent. The create model is Pydantic `extra="forbid"`, and
//      `active` is not one of its fields: sending it is an unknown key → 422.
//      A freshly created plan is active by construction; toggle it with PATCH.
//
// Two further wire rules, both of which 422 when broken:
//   • `monthly_product_credits` is mandatory when `enforces_limit` is true —
//     it is the allowance the enforcement actually reads.
//   • `allowed_formats` must NEVER be `[]`. "All formats" is `null`; `[]` sells
//     nothing and the BE rejects it outright.

// Optional ints (period_days / max_tenants / max_users): '' and null mean
// "not provided" and are omitted; anything else goes as a Number.
function optionalInt(value) {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
}

// "All formats" is null, never []. An empty restricted list cannot be sent (the
// BE 422s on it); PlanModal blocks it before we get here, and this is the
// belt-and-braces so a stray [] can never reach the wire.
function normalizeAllowedFormats(value) {
  if (!Array.isArray(value)) return null;
  const tokens = value.map((tok) => String(tok).trim().toLowerCase()).filter(Boolean);
  return tokens.length ? tokens : null;
}

export async function adminCreatePlan({
  id,
  display_name,
  monthly_credits,
  monthly_product_credits,
  enforces_limit = true,
  renews = true,
  self_serve = false,
  period_days,
  max_tenants,
  max_users,
  allowed_formats,
} = {}) {
  const body = {
    id: String(id ?? '').trim(),
    display_name,
    // Legacy cost scale — kept so pre-change periods stay explicable. It is NOT
    // the customer-facing allowance; sent as a string because the BE stores it
    // as a lossless Decimal.
    monthly_credits: String(monthly_credits ?? 0),
    enforces_limit: !!enforces_limit,
    renews: !!renews,
    self_serve: !!self_serve,
    // null = every format. Never [].
    allowed_formats: normalizeAllowedFormats(allowed_formats),
  };
  // Mandatory only under enforcement; omitted entirely on an unlimited plan,
  // where a null allowance is legitimate.
  if (body.enforces_limit) body.monthly_product_credits = Number(monthly_product_credits);

  const periodDays = optionalInt(period_days);
  if (periodDays !== undefined) body.period_days = periodDays;
  const maxTenants = optionalInt(max_tenants);
  if (maxTenants !== undefined) body.max_tenants = maxTenants;
  const maxUsers = optionalInt(max_users);
  if (maxUsers !== undefined) body.max_users = maxUsers;

  // ⚠️ No `active` key. See the note above — it is forbidden on create.
  const res = await apiFetch(`${ADMIN}/plans`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await readAdminError(res);
    throw buildApiError(err, res.status, { fallbackMessage: `Failed to create plan: ${res.status}` });
  }
  return res.json();
}

// PATCH semantics: an omitted key — and an explicit null — both mean
// "unchanged". `period_days`, `allowed_formats` and `max_users` therefore
// CANNOT be cleared once set, so we drop nulls for them rather than pretending
// the write does something. The UI matches: it offers no control that claims to
// remove those restrictions.
const NON_CLEARABLE_PLAN_FIELDS = ['period_days', 'allowed_formats', 'max_users'];

export async function adminUpdatePlan(planId, patch) {
  const payload = {};
  for (const [key, value] of Object.entries(patch || {})) {
    if (value === undefined) continue;
    if (value === null && NON_CLEARABLE_PLAN_FIELDS.includes(key)) continue;
    payload[key] = value;
  }
  if (payload.monthly_credits != null) payload.monthly_credits = String(payload.monthly_credits);
  if (payload.monthly_product_credits != null) payload.monthly_product_credits = Number(payload.monthly_product_credits);
  if ('allowed_formats' in payload) {
    const formats = normalizeAllowedFormats(payload.allowed_formats);
    // A restricted list that normalises away to nothing would read as "sell
    // everything" — drop the key instead of widening the plan by accident.
    if (formats === null) delete payload.allowed_formats;
    else payload.allowed_formats = formats;
  }
  for (const key of ['period_days', 'max_tenants', 'max_users']) {
    if (key in payload) {
      const n = optionalInt(payload[key]);
      if (n === undefined) delete payload[key];
      else payload[key] = n;
    }
  }

  const res = await apiFetch(`${ADMIN}/plans/${planId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await readAdminError(res);
    if (err.error_code === 'PLAN_NOT_FOUND') throw new Error(i18n.t('errors:admin.planNotFound'));
    throw buildApiError(err, res.status, { fallbackMessage: `Failed to update plan: ${res.status}` });
  }
  return res.json();
}

// Current subscription for the tenant's CUSTOMER (PB2: the wallet is customer-level,
// so the route is tenant-addressed but customer-scoped). Returns null when the
// customer exists but has no active plan (the BE answers 200 null; a 404 from an
// older backend is folded into the same null).
//
// ⛔ 409 TENANT_HAS_NO_CUSTOMER is a DIFFERENT state from null: the tenant is
// attached to no customer at all, so there is no wallet to read or to attach a
// plan to. It is thrown structured (err.code) so the caller renders it as
// "no customer — operator fix" and NOT as "no plan". /credits/balance still
// works for such a tenant and returns customer_id: null.
export async function adminGetTenantSubscription(tenantId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/subscription`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = await readAdminError(res);
    const e = buildApiError(err, res.status, { fallbackMessage: `Failed to fetch subscription: ${res.status}` });
    if (err.error_code === 'TENANT_HAS_NO_CUSTOMER') e.message = i18n.t('errors:admin.tenantHasNoCustomer');
    throw e;
  }
  const data = await res.json();
  return data ?? null;
}

// PUT applies to EVERY tenant the customer owns, not just the addressed one.
// The billing period does NOT restart on every PUT: between two renewing plans
// of the same period length the new row carries started_at over, so the period
// is kept and the new plan applies from the next one. Never write copy that
// claims otherwise.
//
// Errors are thrown structured (err.code, err.status, top-level extras such as
// `missing[]` hoisted by buildApiError) so callers branch on the code:
//   404 PLAN_NOT_FOUND            unknown plan (checked first)
//   409 TENANT_HAS_NO_CUSTOMER    no wallet to attach a plan to (operator CLI fix)
//   409 CUSTOMER_NOT_INVOICEABLE  `missing[]` — fields the customer must fill first
//   409 SUBSCRIPTION_CONFLICT     concurrent reassignment lost the race → refetch, retry
//   TENANT_LIMIT_REACHED is reserved and NOT built — deliberately unhandled.
export async function adminSetTenantSubscription(tenantId, planId) {
  const res = await apiFetch(`${ADMIN}/tenants/${tenantId}/subscription`, {
    method: 'PUT',
    body: JSON.stringify({ plan_id: planId }),
  });
  if (!res.ok) {
    const err = await readAdminError(res);
    const e = buildApiError(err, res.status, { fallbackMessage: `Failed to set subscription: ${res.status}` });
    if (err.error_code === 'PLAN_NOT_FOUND') e.message = i18n.t('errors:admin.planNotFound');
    if (err.error_code === 'SUBSCRIPTION_CONFLICT') e.message = i18n.t('errors:admin.subscriptionConflict');
    if (err.error_code === 'TENANT_HAS_NO_CUSTOMER') e.message = i18n.t('errors:admin.tenantHasNoCustomer');
    throw e;
  }
  return res.json();
}

// ── Customer read + edit (2026-08-20; customer-admin-reachable since 2026-08-24) ──
//
// The only two /api/v1/admin/* routes a NON-superadmin may call: an admin of
// THIS customer (customers[] claim, role 'admin') may read and write its own
// billing record, so a customer can unblock a paid plan without an operator.
//
//   GET  /admin/customers/{id}   → AdminCustomerResponse incl. DERIVED
//                                  missing_billing_fields[] — the same list
//                                  CUSTOMER_NOT_INVOICEABLE carries as missing[],
//                                  so it pre-empts that 409.
//   PATCH /admin/customers/{id}  → omitted key = unchanged, explicit null = clear
//                                  a nullable column, {} = 200 no-op. extra="forbid".
//
// ⛔ 403 CUSTOMER_ADMIN_REQUIRED is answered for a caller who is not an admin of
// this customer AND for an UNKNOWN id (the gate precedes the lookup, so the route
// cannot be used to probe ids). Never render it as "not found". 404
// RESOURCE_NOT_FOUND reaches only a caller who already passed the gate.
// ⛔ `country_code` is superadmin-only on PATCH: a non-superadmin who sends it —
// even as null — gets 422 CUSTOMER_INVALID; it is refused, never stripped. The
// form omits it for non-superadmins; this layer drops it too as belt-and-braces.
export async function adminGetCustomer(customerId) {
  const res = await apiFetch(`${ADMIN}/customers/${customerId}`);
  if (!res.ok) {
    const err = await readAdminError(res);
    throw buildApiError(err, res.status, { fallbackMessage: `Failed to fetch customer: ${res.status}` });
  }
  return res.json();
}

export async function adminUpdateCustomer(customerId, patch, { allowCountryCode = false } = {}) {
  const payload = {};
  for (const [key, value] of Object.entries(patch || {})) {
    if (value === undefined) continue;
    if (key === 'country_code' && !allowCountryCode) continue;
    payload[key] = value;
  }
  const res = await apiFetch(`${ADMIN}/customers/${customerId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await readAdminError(res);
    const e = buildApiError(err, res.status, { fallbackMessage: `Failed to update customer: ${res.status}` });
    if (err.error_code === 'CUSTOMER_VAT_EXISTS') e.message = i18n.t('errors:admin.customerVatExists');
    if (err.error_code === 'CUSTOMER_INVALID' && !err.message) e.message = i18n.t('errors:admin.customerInvalid');
    throw e;
  }
  return res.json();
}
